import React, { useCallback, memo, FC } from 'react';
import { ErrorMessage, Field, FieldProps } from 'formik';
import styled from 'styled-components';
import _find from 'lodash/find';
import Select, { Option } from '@minecraft.select';
import { setFieldValueAndTouched } from '@minecraft.utils';

type Props = {
  saveAsOption?: boolean;
  withErrorMessage?: boolean;
  name: string;
  options: Option[];
  className?: string;
  disabled?: boolean;
  onChange?: (event?: any) => void;
  size?: 'small' | 'large';
  id?: string;
  showLabel?: boolean;
  label?: string;
};

type InnerSelectProps = Props & FieldProps;

/** Styled Components */
export const ErrorArea = styled.div`
  color: ${(props): string => props.theme.colors.accent.red};
  margin: 0.5rem 0;
`;

/** Help Components */
const InnerSelect: FC<InnerSelectProps> = ({
  saveAsOption = false,
  field,
  form,
  options,
  children,
  onChange,
  showLabel = false,
  ...restProps
}) => {
  const handleOnChange = useCallback(
    (event): void => {
      setFieldValueAndTouched(
        field.name,
        saveAsOption ? _find(options, { value: event.target.value }) : event.target.value,
        form
      );
      onChange && onChange(event);
    },
    [onChange, field.name, form]
  );

  return (
    <Select
      value={saveAsOption ? field.value.value : field.value}
      options={options}
      onChange={handleOnChange}
      showLabel={showLabel}
      {...restProps}
    />
  );
};

/**
 * A select component that is connected to Formik
 *
 * @example
 *  <Formik
 *   initialValues={{
 *     color: '',
 *   }}
 *   onSubmit={values => console.log(values)}
 * >
 *   <Form>
 *     <FormikSelect
 *       name="color"
 *       options={[
 *          {
 *            label: 'Red',
 *            value: 'red',
 *          },
 *          {
 *            label: 'Green',
 *            value: 'green',
 *          },
 *          {
 *            label: 'Blue',
 *            value: 'blue',
 *          },
 *        ]}
 *     />
 *     <Button type="submit"> Submit </Button>
 *   </Form>
 * </Formik>
 */

/** @deprecated use SelectInput from '@minecraft.molecules' instead */
const FormikSelect: FC<Props> = ({ withErrorMessage = true, ...restProps }) => {
  return (
    <>
      <Field {...restProps}>{(fieldProps): JSX.Element => <InnerSelect {...restProps} {...fieldProps} />}</Field>
      {withErrorMessage && (
        <ErrorArea>
          <ErrorMessage name={restProps.name} />
        </ErrorArea>
      )}
    </>
  );
};

/** @deprecated use SelectInput from '@minecraft.molecules' instead */
export default memo<Props>(FormikSelect);
